// BoardGameGeek API Service
class BGGService {
  constructor() {
    // Use backend proxy instead of direct BGG API with CORS proxies
    this.backendURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    this.cache = new Map();
    this.rateLimitDelay = 1000; // 1 second between requests to respect BGG API limits
    this.lastRequestTime = 0;
  }

  async delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async rateLimit() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.rateLimitDelay) {
      await this.delay(this.rateLimitDelay - timeSinceLastRequest);
    }
    this.lastRequestTime = Date.now();
  }

  async searchGame(gameName) {
    // Check cache first
    const cacheKey = `search_${gameName.toLowerCase()}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      await this.rateLimit();
      
      console.log(`Searching BGG for: ${gameName}`);
      
      // Call backend proxy instead of using CORS proxies
      const response = await fetch(
        `${this.backendURL}/api/bgg/search?query=${encodeURIComponent(gameName)}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const xmlText = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      
      const items = xmlDoc.getElementsByTagName('item');
      
      if (items.length === 0) {
        console.log(`No BGG results found for: ${gameName}`);
        this.cache.set(cacheKey, null);
        return null;
      }

      // Get the first (best) match
      const firstItem = items[0];
      const gameId = firstItem.getAttribute('id');
      const gameName_result = firstItem.getElementsByTagName('name')[0]?.getAttribute('value');
      
      console.log(`Found BGG match: ${gameName_result} (ID: ${gameId})`);
      
      const gameResult = {
        id: gameId,
        name: gameName_result
      };
      
      this.cache.set(cacheKey, gameResult);
      return gameResult;
      
    } catch (error) {
      console.error(`Error searching BGG for ${gameName}:`, error);
      this.cache.set(cacheKey, null);
      return null;
    }
  }

  async getGameDetails(gameId) {
    // Check cache first
    const cacheKey = `details_${gameId}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      await this.rateLimit();
      
      console.log(`Fetching BGG details for game ID: ${gameId}`);
      
      // Call backend proxy
      const response = await fetch(
        `${this.backendURL}/api/bgg/game/${gameId}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      console.log(`Successfully retrieved data from backend proxy`);
      const xmlText = await response.text();
      
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      
      // Check for XML parsing errors
      const parserError = xmlDoc.getElementsByTagName('parsererror')[0];
      if (parserError) {
        throw new Error(`XML parsing error: ${parserError.textContent}`);
      }
      
      const items = xmlDoc.getElementsByTagName('item');
      console.log(`Found ${items.length} BGG items`);
      
      const item = items[0];
      if (!item) {
        console.log('No BGG item found in response');
        this.cache.set(cacheKey, null);
        return null;
      }
      
      console.log(`Processing BGG item with ID: ${item.getAttribute('id')}`);

      // Extract game details
      const name = item.getElementsByTagName('name')[0]?.getAttribute('value');
      const image = item.getElementsByTagName('image')[0]?.textContent;
      const thumbnail = item.getElementsByTagName('thumbnail')[0]?.textContent;
      const description = item.getElementsByTagName('description')[0]?.textContent;
      const yearPublished = item.getElementsByTagName('yearpublished')[0]?.getAttribute('value');
      
      // Get player count
      const minPlayers = item.getElementsByTagName('minplayers')[0]?.getAttribute('value');
      const maxPlayers = item.getElementsByTagName('maxplayers')[0]?.getAttribute('value');
      
      // Get playtime
      const minPlaytime = item.getElementsByTagName('minplaytime')[0]?.getAttribute('value');
      const maxPlaytime = item.getElementsByTagName('maxplaytime')[0]?.getAttribute('value');
      const playingTime = item.getElementsByTagName('playingtime')[0]?.getAttribute('value');
      
      // Get complexity (weight) and rating
      const stats = item.getElementsByTagName('statistics')[0];
      const ratings = stats?.getElementsByTagName('ratings')[0];
      const averageweight = ratings?.getElementsByTagName('averageweight')[0]?.getAttribute('value');
      const average = ratings?.getElementsByTagName('average')[0]?.getAttribute('value');
      
      // Get categories and mechanics for tags
      const links = item.getElementsByTagName('link');
      const categories = [];
      const mechanics = [];
      
      for (let link of links) {
        const type = link.getAttribute('type');
        const value = link.getAttribute('value');
        
        if (type === 'boardgamecategory') {
          categories.push(value.toLowerCase());
        } else if (type === 'boardgamemechanic') {
          mechanics.push(value.toLowerCase().replace(/\s+/g, ' ').trim());
        }
      }

      const gameDetails = {
        id: gameId,
        name: name,
        image: image,
        thumbnail: thumbnail,
        description: this.stripHTML(description),
        yearPublished: yearPublished ? parseInt(yearPublished) : null,
        players: {
          min: minPlayers ? parseInt(minPlayers) : 1,
          max: maxPlayers ? parseInt(maxPlayers) : 4
        },
        playtime: playingTime ? parseInt(playingTime) : (maxPlaytime ? parseInt(maxPlaytime) : 60),
        complexity: averageweight ? parseFloat(averageweight) : 2.5,
        rating: average ? parseFloat(average) : null,
        tags: [...categories.slice(0, 3), ...mechanics.slice(0, 2)].slice(0, 5),
        bggId: gameId
      };

      console.log(`BGG details fetched for: ${name}`, gameDetails);
      
      this.cache.set(cacheKey, gameDetails);
      return gameDetails;
      
    } catch (error) {
      console.error(`Error fetching BGG details for game ID ${gameId}:`, error);
      
      // Provide more specific error information
      if (error.name === 'AbortError') {
        console.error('BGG request timed out');
      } else if (error.message.includes('Failed to fetch')) {
        console.error('Network error or CORS issue');
      } else if (error.message.includes('XML parsing error')) {
        console.error('Invalid XML response from BGG');
      }
      
      this.cache.set(cacheKey, null);
      return null;
    }
  }

  async searchAndGetGameData(gameName) {
    try {
      // First search for the game
      const searchResult = await this.searchGame(gameName);
      if (!searchResult) {
        return null;
      }

      // Then get detailed information
      const gameDetails = await this.getGameDetails(searchResult.id);
      return gameDetails;
      
    } catch (error) {
      console.error(`Error getting BGG data for ${gameName}:`, error);
      return null;
    }
  }

  stripHTML(html) {
    if (!html) return '';
    
    // Remove HTML tags and decode entities
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    
    // Get text content and clean up
    let text = tmp.textContent || tmp.innerText || '';
    
    // Limit description length
    if (text.length > 200) {
      text = text.substring(0, 200).trim() + '...';
    }
    
    return text;
  }

  // Method to enrich existing game data with BGG information
  async enrichGameWithBGG(game) {
    try {
      const bggData = await this.searchAndGetGameData(game.name);
      
      if (bggData) {
        console.log(`Enriching ${game.name} with BGG data:`, bggData);
        
        return {
          ...game,
          
          // PRESERVE GAME STATUS & USER SETTINGS - Never change these fields during enrichment
          owned: game.owned,
          wishlist: game.wishlist,
          try: game.try,
          isCoOp: game.isCoOp, // Preserve co-op setting
          
          // Always update image if BGG has one
          image: bggData.image || bggData.thumbnail || game.image,
          bggId: bggData.bggId,
          
          // Update description if BGG has better/more detailed data
          description: bggData.description && bggData.description.length > 20 
            ? bggData.description 
            : game.description,
          
          // Update player count from BGG
          players: {
            min: bggData.players?.min || game.players?.min || 1,
            max: bggData.players?.max || game.players?.max || 4
          },
          
          // Update playtime from BGG
          playtime: bggData.playtime && bggData.playtime > 0 
            ? bggData.playtime 
            : game.playtime,
          
          // Update complexity from BGG (BGG data is usually more accurate)
          complexity: bggData.complexity && bggData.complexity > 0 && bggData.complexity <= 5
            ? parseFloat(bggData.complexity.toFixed(1))
            : game.complexity,
          
          // Merge tags - combine user tags with BGG tags (remove duplicates)
          tags: bggData.tags && bggData.tags.length > 0 
            ? [...new Set([...(game.tags || []), ...bggData.tags])]
            : game.tags,
          
          // Update rating from BGG
          rating: bggData.rating && bggData.rating > 0 
            ? parseFloat(bggData.rating.toFixed(1))
            : game.rating,
          
          // Add additional metadata
          yearPublished: bggData.yearPublished,
          
          // Mark as BGG enriched
          bggEnriched: true,
          bggEnrichedAt: new Date().toISOString()
        };
      }
      
      return game;
    } catch (error) {
      console.error(`Error enriching game ${game.name}:`, error);
      return game;
    }
  }

  // Batch enrich multiple games with rate limiting
  async enrichGamesWithBGG(games, onProgress = null) {
    const enrichedGames = [];
    let successCount = 0;
    let failureCount = 0;
    
    console.log(`Starting BGG enrichment for ${games.length} games`);
    
    for (let i = 0; i < games.length; i++) {
      const game = games[i];
      
      if (onProgress) {
        onProgress(i + 1, games.length, game.name);
      }
      
      try {
        console.log(`Processing game ${i + 1}/${games.length}: ${game.name}`);
        
        // Skip if already has BGG content (preserve manually-set or existing BGG data)
        // This includes: 1) Games that went through bulk enrichment, 2) Games with manually-added BGG IDs
        if ((game.bggEnriched && game.bggId) || (game.bggId && game.bggId.trim() !== '')) {
          const reason = game.bggEnriched ? 'already enriched' : 'has manually-set BGG ID';
          console.log(`Skipping ${game.name} - ${reason} (ID: ${game.bggId})`);
          enrichedGames.push(game);
          continue;
        }
        
        const enrichedGame = await this.enrichGameWithBGG(game);
        enrichedGames.push(enrichedGame);
        
        if (enrichedGame.bggEnriched) {
          successCount++;
          console.log(`✅ Successfully enriched: ${game.name}`);
        } else {
          failureCount++;
          console.log(`❌ Failed to enrich: ${game.name}`);
        }
        
        // Add extra delay between requests to be more respectful to BGG
        if (i < games.length - 1) {
          await this.delay(1500); // 1.5 seconds between games
        }
        
      } catch (error) {
        console.error(`Failed to enrich ${game.name}:`, error);
        enrichedGames.push(game); // Keep original game if enrichment fails
        failureCount++;
      }
    }
    
    console.log(`BGG enrichment complete. Success: ${successCount}, Failed: ${failureCount}`);
    return enrichedGames;
  }

  clearCache() {
    this.cache.clear();
  }
}

export default new BGGService();